/**
 * Content of the viewer, in a declarative but concrete form.
 * 
 * All types start with V (from Viewer) to avoid collision with the types of the
 * 3D engines, as concepts are the same (Colors, Scenes, etc)
 */

export type VColor = [ number, number, number ] | [ number, number, number, number ];
export type VCoord = [ number, number, number ];
export type VRotation = [ number, number, number ];
export type VScale = [ number, number, number ];

export const DEFAULT_POSITION: VCoord = [ 0, 0, 0 ];
export const DEFAULT_ROTATION: VRotation = [ 0, 0, 0 ];
export const DEFAULT_SCALE: VRotation = [ 1, 1, 1 ];
export const DEFAULT_IS_ICON = false;
export const DEFAULT_IS_BILLBOARD = false;
export const DEFAULT_HIDE = false;
export const DEFAULT_TEXTURE_SCALE = 1;
export const DEFAULT_CLICKABLE = false;

export type VGroupRelativePositionSide = 'front' | 'back' | 'left' | 'right' | 'top';
export type VGroupRelativePosition = {
    referenceGroup: string
    side: VGroupRelativePositionSide
    align?: VGroupRelativePositionSide
    offset?: number
}

export type VGroupPosition = VCoord | VGroupRelativePosition;

export type VLocation = string | {
    package?: string
    filename: string
}

export interface VRelativePositioningBase {
    referencePart: string
    delta: VCoord
};

export interface VRelativePositioningMeshes extends VRelativePositioningBase {
    referenceMesh: string
    mesh: string
}

export type VRelativePositioningPart = VRelativePositioningBase;

export interface VRelativePositioningBoundingBox extends VRelativePositioningBase {
    bbox: 'top'
}

export type VRelativePositioning = VRelativePositioningPart | VRelativePositioningMeshes | VRelativePositioningBoundingBox;


export type VPartPositioning = VCoord | VRelativePositioning;

export interface VMaterialCommon {
    // use to avoid re-creating the materials
    name?: string
    alpha: number
    basecolor: [ number, number, number ]
    anisotropy: {
        rotation: number
        value: number
    }
    clearcoat: {
        roughness: number
        value: number
    }
    displacement: number
    ior: number
    metalness: number
    metallicReflectanceColor: [ number, number, number ]
    roughness: number
    sheen: {
        value: number
    }
    specular: {
        value: number
    }
}

export interface VMaterial extends VMaterialCommon {
    baseAndAlpha?: VLocation
    metalnessAndRoughness?: VLocation
    normalAndHeight?: VLocation
}

export interface VMeshRuleCommon {
    test: string
    hide?: boolean
    textureScale?: number
    isIcon?: boolean
    clickable?: boolean
    ignoreInLayout?: boolean
    data?: any
}

export interface VMeshRule extends VMeshRuleCommon {
    material?: VMaterial
    aoMap?: VLocation
}

export interface VPart {
    id: string
    model: VLocation
    position: VPartPositioning
    rotation: VRotation
    scale: VScale
    animationPercentage?: number
    animationFixed: boolean
    meshes: VMeshRule[]
}

export interface VItemContainer {
    items: VItem[]
}

export type VGroupSpatialInfo  = {
    position?: VGroupPosition
    rotation?: VRotation, 
    scale?: VScale
    isBillboard?: boolean
    fixedSizeFactor?: number
}

export type VDimensionsAttibute = [ number|undefined, number|undefined, number|undefined ]

export type VGroupAttributes = {
    clickable: boolean
    moveable: boolean
    showDimensions: boolean
    dimensions?: VDimensionsAttibute
    useInBoundingBox: boolean
    hideInSnapshot: boolean
    data?: any
}

export interface VGroup extends VItemContainer {
    id: string
    position: VGroupSpatialInfo
    attributes: VGroupAttributes
}

export type VItem = VGroup | VPart;

export interface VSceneContent extends VItemContainer {
    backgroundColor?: VColor
    environment?: VLocation
    lights: {
        hemisphere: boolean
    }
    directionalShadow: boolean
    shadowCatcher: boolean
    globalDimensions: boolean
}

export interface VCamera {
    target: VCoord
    position: VCoord
}
export interface VThumbnailInfo {
    show: boolean
    camera: VCamera
    position: [ number, number ],
    size: [ number, number ]
}

export interface VScene {
    content: VSceneContent
    camera: VCamera
    thumbnailView?: VThumbnailInfo
}

export interface Viewer3DContent {
    qualitySettings: any
    scenes: Record<string,VScene>
    // Elements to be observed and dimensions notified
    watchedNodes: string[]
}

/*
 * The "Loaded" variants are for the imperative interface. Instead of
 * poiting to locations, they contain the blobURLs, but they also
 * have the extension of the file, for the engine to know which format
 * to use. 
 */

export type VLocationLoaded = {
    url: string
    ext: string
}

export interface VMaterialLoaded extends VMaterialCommon {
    baseAndAlpha?: VLocationLoaded
    metalnessAndRoughness?: VLocationLoaded
    normalAndHeight?: VLocationLoaded
}

export interface VMeshRuleLoaded extends VMeshRuleCommon {
    material?: VMaterialLoaded
    aoMap?: VLocationLoaded
}

// Notification types

export interface ElementLayoutResult {
    position: VCoord
    rotation: VRotation
}

export interface WatchedElementResult {
    boundingBox: [ VCoord, VCoord ]
    dimensions: [ number, number ]
}